/**
 * OPFS-backed storage chassis.
 *
 *  - main vault: wasiyat-vault.enc (encrypted JSON envelope)
 *  - blobs:      blobs/<blob_id> (encrypted document files)
 *  - meta:       wasiyat-meta.json (non-secret: passkey handle, last-unlock, build)
 *
 * Falls back to localStorage for envelope-only on engines without OPFS
 * (the document vault degrades gracefully).
 */

import type { VaultEnvelope } from './crypto.ts';
import type { BlobId } from '../types.ts';

const ENVELOPE_FILE = 'wasiyat-vault.enc';
const META_FILE = 'wasiyat-meta.json';
const BLOBS_DIR = 'blobs';
const LS_KEY = 'wasiyat:vault';

interface VaultMeta {
  passkey_handle_b64: string | null;
  last_unlock_at: string;
  build: string;
}

async function opfs(): Promise<FileSystemDirectoryHandle | null> {
  if (!('storage' in navigator) || !navigator.storage.getDirectory) return null;
  try {
    return await navigator.storage.getDirectory();
  } catch {
    return null;
  }
}

export async function saveEnvelope(env: VaultEnvelope): Promise<void> {
  const dir = await opfs();
  if (dir) {
    const handle = await dir.getFileHandle(ENVELOPE_FILE, { create: true });
    const writable = await handle.createWritable();
    await writable.write(JSON.stringify(env));
    await writable.close();
    return;
  }
  localStorage.setItem(LS_KEY, JSON.stringify(env));
}

export async function loadEnvelope(): Promise<VaultEnvelope | null> {
  const dir = await opfs();
  if (dir) {
    try {
      const handle = await dir.getFileHandle(ENVELOPE_FILE);
      const file = await handle.getFile();
      const text = await file.text();
      return JSON.parse(text) as VaultEnvelope;
    } catch {
      // fall through to LS
    }
  }
  const ls = localStorage.getItem(LS_KEY);
  return ls ? (JSON.parse(ls) as VaultEnvelope) : null;
}

export async function clearEnvelope(): Promise<void> {
  const dir = await opfs();
  if (dir) {
    try {
      await dir.removeEntry(ENVELOPE_FILE);
    } catch {
      // ignore
    }
  }
  localStorage.removeItem(LS_KEY);
}

export async function saveMeta(meta: VaultMeta): Promise<void> {
  const dir = await opfs();
  if (!dir) {
    localStorage.setItem('wasiyat:meta', JSON.stringify(meta));
    return;
  }
  const handle = await dir.getFileHandle(META_FILE, { create: true });
  const writable = await handle.createWritable();
  await writable.write(JSON.stringify(meta));
  await writable.close();
}

export async function loadMeta(): Promise<VaultMeta | null> {
  const dir = await opfs();
  if (!dir) {
    const v = localStorage.getItem('wasiyat:meta');
    return v ? (JSON.parse(v) as VaultMeta) : null;
  }
  try {
    const handle = await dir.getFileHandle(META_FILE);
    const file = await handle.getFile();
    return JSON.parse(await file.text()) as VaultMeta;
  } catch {
    return null;
  }
}

export async function ensureBlobsDir(): Promise<FileSystemDirectoryHandle | null> {
  const dir = await opfs();
  if (!dir) return null;
  return dir.getDirectoryHandle(BLOBS_DIR, { create: true });
}

export async function saveBlob(id: BlobId, bytes: Uint8Array): Promise<boolean> {
  const blobs = await ensureBlobsDir();
  if (!blobs) return false;
  const file = await blobs.getFileHandle(id, { create: true });
  const writable = await file.createWritable();
  await writable.write(bytes);
  await writable.close();
  return true;
}

export async function loadBlob(id: BlobId): Promise<Uint8Array | null> {
  const blobs = await ensureBlobsDir();
  if (!blobs) return null;
  try {
    const file = await blobs.getFileHandle(id);
    const f = await file.getFile();
    return new Uint8Array(await f.arrayBuffer());
  } catch {
    return null;
  }
}

export async function deleteBlob(id: BlobId): Promise<void> {
  const blobs = await ensureBlobsDir();
  if (!blobs) return;
  try {
    await blobs.removeEntry(id);
  } catch {
    // ignore
  }
}

export async function listBlobs(): Promise<string[]> {
  const blobs = await ensureBlobsDir();
  if (!blobs) return [];
  const out: string[] = [];
  // @ts-expect-error: entries() is in the spec but lib.dom lags on some TS versions
  for await (const [name] of blobs.entries()) out.push(name as string);
  return out;
}

export async function storageEstimate(): Promise<{ usage_mb: number; quota_mb: number } | null> {
  if (!('storage' in navigator) || !navigator.storage.estimate) return null;
  const est = await navigator.storage.estimate();
  const u = est.usage ?? 0;
  const q = est.quota ?? 0;
  return { usage_mb: Math.round(u / 1024 / 1024 * 100) / 100, quota_mb: Math.round(q / 1024 / 1024) };
}
